/**
 * La pista horaria del parte, convertida en algo que se puede pintar.
 *
 * REGLA DE ORO: aquí no entra `new Date()` para leer fechas del parte. Open-Meteo
 * manda las horas en hora LOCAL del sitio y sin sufijo de zona («2026-08-25T14:00»),
 * así que el prefijo del string ES la fecha local y la hora se lee del texto. Pasar
 * eso por `Date` lo interpreta el navegador en SU huso y corre el día — es el mismo
 * fallo que obligó a escribir `pickToday`, y el guardián lo vigila.
 *
 * `Date` sólo aparece para saber qué hora es AHORA en el sitio consultado
 * (`nowLocalIso`), que es lo único que no viene en el payload.
 */
import type {
  N8nWeatherDay,
  N8nWeatherHourly,
  N8nWeatherCurrent,
  N8nWeatherNowSeries,
  N8nWeatherPayload,
  WeatherCondition,
} from "@/lib/weather";

/** Una hora de la ventana, ya lista para pintar. `null` = no hay dato; se pinta «—». */
export type HourPoint = {
  /** «YYYY-MM-DDTHH:mm», hora local del sitio. */
  iso: string;
  /** «YYYY-MM-DD». */
  date: string;
  /** 0..23. */
  hour: number;
  temp: number | null;
  feelsLike: number | null;
  humidity: number | null;
  /** Lluvia de ESA hora (mm). */
  precipMm: number | null;
  precipProbPct: number | null;
  windMs: number | null;
  uvIndex: number | null;
  solarWm2: number | null;
  isDay: boolean;
  condition: WeatherCondition | null;
};

/**
 * Las 48 h que enseña el detalle: el día elegido entero y el siguiente, para poder
 * arrastrar más allá de medianoche (que es justo lo que se ve en la app de
 * referencia: el cursor puesto en «V 05:00» dentro de la ficha del jueves).
 */
export type HourWindow = {
  points: HourPoint[];
  /** Primer índice del día SIGUIENTE, o -1 si la ventana no llega. Ahí va la raya. */
  nextDayStart: number;
  /** Índice de la hora de AHORA, o -1 si ahora no cae dentro de la ventana. */
  nowIndex: number;
  /**
   * Máxima y mínima DEL DÍA ELEGIDO — no de las 48 h. El titular enseña la máxima y
   * la mínima de ese día, y si la gráfica anotase el pico del día siguiente estaría
   * contradiciendo al titular con un número que no es del día que se está mirando.
   */
  maxIndex: number;
  minIndex: number;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Qué hora es AHORA en el sitio consultado, en el mismo formato que el parte
 * («YYYY-MM-DDTHH:mm»). Es lo único que no viene en el payload.
 */
export function nowLocalIso(timezone?: string): string {
  const now = new Date();
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    // Algunas implementaciones dan «24» para la medianoche con hour12:false.
    const hh = get("hour") === "24" ? "00" : get("hour");
    const iso = `${get("year")}-${get("month")}-${get("day")}T${hh}:${get("minute")}`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(iso)) return iso;
  } catch {
    // Intl sin esa zona: se sigue con la hora del navegador.
  }
  return (
    `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}` +
    `T${pad2(now.getHours())}:${pad2(now.getMinutes())}`
  );
}

/** Amanecer y ocaso por fecha, sacados de los días del parte. */
export type SunTimes = { sunrise: string | null; sunset: string | null };

export function sunTimesByDate(days: N8nWeatherDay[] | null | undefined): Record<string, SunTimes> {
  const out: Record<string, SunTimes> = {};
  for (const d of days ?? []) {
    out[d.date] = { sunrise: d.sunrise_iso ?? null, sunset: d.sunset_iso ?? null };
  }
  return out;
}

/**
 * ¿Es de día a esa hora? Manda lo que diga el proveedor; si no lo dijo, se compara
 * con el amanecer y el ocaso de ESE día (comparación de textos, que en este formato
 * ordena igual que el tiempo). Sin ninguna de las dos cosas, se asume de día: el
 * cielo saldrá claro, que es menos raro que un mediodía pintado de noche.
 */
function resolveIsDay(
  flag: 0 | 1 | null | undefined,
  iso: string,
  sun: SunTimes | undefined,
): boolean {
  if (flag === 0) return false;
  if (flag === 1) return true;
  if (sun?.sunrise && sun?.sunset) return iso >= sun.sunrise && iso < sun.sunset;
  return true;
}

/** El día siguiente a una fecha «YYYY-MM-DD», sin tocar husos. */
export function nextDate(dateIso: string): string {
  const [y, m, d] = dateIso.split("-").map(Number);
  // Mediodía UTC: inmune al horario de verano, que a medianoche puede saltar de día.
  const t = new Date(Date.UTC(y, (m || 1) - 1, d || 1, 12));
  t.setUTCDate(t.getUTCDate() + 1);
  return t.toISOString().slice(0, 10);
}

/** La hora `i` de la pista, lista para pintar. `null` si su sello no se puede leer. */
function pointAt(
  hourly: N8nWeatherHourly,
  i: number,
  legend: N8nWeatherHourly["condition_legend"],
  sun: Record<string, SunTimes> | undefined,
): HourPoint | null {
  const iso = hourly.time[i];
  if (typeof iso !== "string") return null;
  const date = iso.slice(0, 10);
  const hour = Number(iso.slice(11, 13));
  if (!Number.isFinite(hour)) return null;

  const isDay = resolveIsDay(hourly.is_day?.[i], iso, sun?.[date]);
  const code = hourly.code?.[i];
  const entry = code != null ? legend[String(code)] : undefined;
  const condition: WeatherCondition | null = entry
    ? {
      id: entry.id,
      main: entry.main,
      description: entry.description,
      icon: `${entry.icon_base}${isDay ? "d" : "n"}`,
    }
    : null;

  return {
    iso,
    date,
    hour,
    temp: hourly.temp?.[i] ?? null,
    feelsLike: hourly.feels_like?.[i] ?? null,
    humidity: hourly.humidity?.[i] ?? null,
    precipMm: hourly.precip_mm?.[i] ?? null,
    precipProbPct: hourly.precip_prob_pct?.[i] ?? null,
    windMs: hourly.wind_ms?.[i] ?? null,
    uvIndex: hourly.uv_index?.[i] ?? null,
    solarWm2: hourly.solar_Wm2?.[i] ?? null,
    isDay,
    condition,
  };
}

/**
 * Las próximas `count` horas desde la hora de AHORA (incluida), en orden. Es lo que
 * enseña la tira «Por horas» del héroe (2026-09-25). Vacía si el parte no trae pista
 * horaria (función vieja desplegada): entonces el héroe solo ofrece la semana.
 */
export function buildNextHours(
  hourly: N8nWeatherHourly | null | undefined,
  opts: { nowIso: string; sun?: Record<string, SunTimes>; count?: number },
): HourPoint[] {
  if (!hourly || !Array.isArray(hourly.time) || hourly.time.length === 0) return [];
  // «14:37» cae en la hora de las 14:00: se compara con el sello en punto.
  const hourStart = `${opts.nowIso.slice(0, 13)}:00`;
  const legend = hourly.condition_legend ?? {};
  const out: HourPoint[] = [];
  for (let i = 0; i < hourly.time.length; i++) {
    const iso = hourly.time[i];
    if (typeof iso !== "string" || iso < hourStart) continue;
    const p = pointAt(hourly, i, legend, opts.sun);
    if (p) out.push(p);
  }
  out.sort((a, b) => (a.iso < b.iso ? -1 : a.iso > b.iso ? 1 : 0));
  return out.slice(0, opts.count ?? 24);
}

/**
 * Misma regla que `isDewFogHour` del servidor (_shared/weather.ts): niebla según el
 * modelo, o aire casi saturado y en calma. Las tres apps la comparten.
 */
export const DEW_RH_PCT = 92;
export const DEW_WIND_MS = 2.5;

export function isDewFogPoint(p: HourPoint): boolean {
  // 741 = niebla (WMO 45/48 traducido al código de OpenWeather en el servidor).
  if (p.condition?.id === 741) return true;
  return p.humidity != null && p.windMs != null && p.humidity >= DEW_RH_PCT && p.windMs <= DEW_WIND_MS;
}

/**
 * Primera hora con rocío/niebla probable dentro de las próximas `withinHours` horas.
 * `null` si no hay ninguna: entonces no se avisa, aunque la hubiera de madrugada.
 */
export function nextDewFogHour(points: HourPoint[], withinHours = 12): HourPoint | null {
  return points.slice(0, withinHours).find(isDewFogPoint) ?? null;
}

/**
 * Construye la ventana de 48 h que arranca en el día elegido. Devuelve `null` cuando
 * el parte no trae hora ninguna de ese día: entonces el detalle se cae a la ficha
 * estática, que es lo honesto — mejor la ficha de siempre que una curva inventada.
 */
export function buildHourWindow(
  hourly: N8nWeatherHourly | null | undefined,
  dateIso: string,
  opts: { nowIso: string; sun?: Record<string, SunTimes> } ,
): HourWindow | null {
  if (!hourly || !Array.isArray(hourly.time) || hourly.time.length === 0) return null;

  const tomorrow = nextDate(dateIso);
  const legend = hourly.condition_legend ?? {};
  const points: HourPoint[] = [];

  for (let i = 0; i < hourly.time.length; i++) {
    const iso = hourly.time[i];
    if (typeof iso !== "string") continue;
    const date = iso.slice(0, 10);
    if (date !== dateIso && date !== tomorrow) continue;
    const p = pointAt(hourly, i, legend, opts.sun);
    if (p) points.push(p);
  }

  // La pista puede venir desordenada de una caché mezclada; el orden lo decide el
  // texto, que en este formato ordena igual que el tiempo.
  points.sort((a, b) => (a.iso < b.iso ? -1 : a.iso > b.iso ? 1 : 0));

  const ownDay = points.filter((p) => p.date === dateIso);
  if (ownDay.length === 0) return null;

  let maxIndex = -1;
  let minIndex = -1;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (p.date !== dateIso || p.temp == null) continue;
    if (maxIndex < 0 || p.temp > (points[maxIndex].temp as number)) maxIndex = i;
    if (minIndex < 0 || p.temp < (points[minIndex].temp as number)) minIndex = i;
  }

  return {
    points,
    nextDayStart: points.findIndex((p) => p.date === tomorrow),
    nowIndex: hourIndexForIso(points, opts.nowIso),
    maxIndex,
    minIndex,
  };
}

/**
 * Índice de la hora que contiene un instante: la ÚLTIMA hora que no lo ha pasado.
 * Fuera de la ventana devuelve -1 — no se acerca al borde más próximo, porque
 * «ahora» pintado en un día que no es hoy sería mentira.
 */
export function hourIndexForIso(points: HourPoint[], iso: string): number {
  let found = -1;
  for (let i = 0; i < points.length; i++) {
    if (points[i].iso <= iso) found = i;
    else break;
  }
  // Antes del primer punto de la ventana: no es «la primera hora», es que no cae aquí.
  if (found < 0) return -1;
  return found;
}

/**
 * Dónde poner el cursor al abrir un día: en la hora de ahora si el día es hoy, y si
 * no en las 12:00, que es la hora que resume un día para quien cultiva.
 */
export function defaultCursorIndex(win: HourWindow, dateIso: string): number {
  if (win.nowIndex >= 0) return win.nowIndex;
  const noon = win.points.findIndex((p) => p.date === dateIso && p.hour === 12);
  if (noon >= 0) return noon;
  return 0;
}

/** El tiempo de ahora sacado de los tramos de 15 min. */
export type NowPoint = {
  iso: string;
  temp: number | null;
  feelsLike: number | null;
  humidity: number | null;
  windMs: number | null;
  gustMs: number | null;
  condition: WeatherCondition | null;
};

/**
 * El tramo de 15 min que contiene la hora de ahora (el último que no la ha pasado), en el
 * momento de PINTAR (2026-09-26). Es lo que hace que Pest, Tactic y Ulture Go digan lo
 * mismo a la vez aunque cada una trajera el parte en un minuto distinto. `null` si la
 * serie no llega o si ahora cae fuera de ella (parte demasiado viejo): entonces manda
 * `current`, como antes.
 */
export function pickNow(series: N8nWeatherNowSeries | null | undefined, nowIso: string): NowPoint | null {
  if (!series || !Array.isArray(series.time) || series.time.length === 0) return null;
  let idx = -1;
  for (let i = 0; i < series.time.length; i++) {
    if (series.time[i] <= nowIso) idx = i;
    else break;
  }
  // Antes del primer tramo, o pasado el final del último: la serie no cubre ahora.
  if (idx < 0) return null;
  if (idx === series.time.length - 1) {
    // Las dos marcas son hora LOCAL del sitio; se comparan como si fueran UTC, sin husos.
    const end = Date.parse(`${series.time[idx]}:00Z`) + 15 * 60_000;
    if (!(Date.parse(`${nowIso}:00Z`) < end)) return null;
  }
  const code = series.code?.[idx];
  const entry = code != null ? series.condition_legend?.[String(code)] : undefined;
  const isDay = series.is_day?.[idx] !== 0;
  return {
    iso: series.time[idx],
    temp: series.temp?.[idx] ?? null,
    feelsLike: series.feels_like?.[idx] ?? null,
    humidity: series.humidity?.[idx] ?? null,
    windMs: series.wind_ms?.[idx] ?? null,
    gustMs: series.gust_ms?.[idx] ?? null,
    condition: entry
      ? { id: entry.id, main: entry.main, description: entry.description, icon: `${entry.icon_base}${isDay ? "d" : "n"}` }
      : null,
  };
}

/**
 * El `current` que se enseña: el del parte con los números del tramo de 15 min de ESTE
 * minuto encima. Único sitio que lo decide (héroe y bienvenida), para que no diverjan.
 */
export function liveCurrent(data: Pick<N8nWeatherPayload, "current" | "now_series" | "location"> | null | undefined): N8nWeatherCurrent | null {
  const base = data?.current ?? null;
  if (!data || !base) return base;
  const p = pickNow(data.now_series, nowLocalIso(data.location?.timezone));
  if (!p) return base;
  return {
    ...base,
    temp: p.temp ?? base.temp,
    feels_like: p.feelsLike ?? base.feels_like,
    humidity: p.humidity ?? base.humidity,
    viento_vel_ms: p.windMs ?? base.viento_vel_ms,
    viento_rachas_ms: p.gustMs ?? base.viento_rachas_ms,
    condition: p.condition ?? base.condition,
  };
}
